import { motion } from "framer-motion";
import { ShieldCheck, Calculator, Search, Scale, Check, X, MessageCircle } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { waLink, WA_MESSAGES } from "../lib/whatsapp";

const PILLARS = [
  {
    icon: ShieldCheck,
    title: "Blindagem patrimonial estratégica",
    text: "Estruturas jurídicas lícitas (holdings, pactos e regimes adequados) para proteger bens, empresas e investimentos antes, durante e depois do divórcio.",
  },
  {
    icon: Calculator,
    title: "Redução tributária inteligente",
    text: "Planejamento da partilha para minimizar ITCMD sobre excedente de meação e ganho de capital, preservando o máximo do seu patrimônio dentro da lei.",
  },
  {
    icon: Search,
    title: "Mapeamento e localização de bens",
    text: "Investigação patrimonial minuciosa para identificar bens subavaliados, ocultados ou omitidos pela outra parte, inclusive contas e aplicações.",
  },
  {
    icon: Scale,
    title: "Negociação confidencial & litígio",
    text: "Condução firme e discreta: acordos estruturados quando possível, litígio estratégico quando necessário. Sempre com sigilo absoluto.",
  },
];

const COMPARISON = [
  { sem: "Partilha no automático, sem avaliação técnica.", com: "Avaliação independente de cada bem e quota." },
  { sem: "ITCMD e IR pagos além do necessário.", com: "Planejamento tributário antes da partilha." },
  { sem: "Contas expostas e risco de bloqueio.", com: "Estratégia de liquidez e proteção de contas." },
  { sem: "Acordos assinados sob pressão emocional.", com: "Negociação fria, técnica e com prazos controlados." },
  { sem: "Bens ocultos que nunca aparecem.", com: "Investigação patrimonial completa." },
];

export const Solutions = () => (
  <section id="solucoes" data-testid="solutions-section" className="relative py-24 sm:py-32 bg-[#0B1320]">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <SectionHeading
        eyebrow="Soluções estratégicas TSS"
        title="A diferença entre reagir ao divórcio e conduzi-lo."
        description="Quatro frentes de atuação integradas para que nenhum real do seu patrimônio fique desprotegido."
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-14" data-testid="solutions-pillars">
        {PILLARS.map((p, i) => (
          <Reveal key={p.title} delay={i * 0.08}>
            <motion.div
              whileHover={{ y: -6 }}
              transition={{ duration: 0.3 }}
              data-testid={`solution-pillar-${i}`}
              className="h-full border border-amber-500/15 bg-[#111C2E] rounded-2xl p-7 hover:border-amber-400/40 transition-colors duration-500"
            >
              <div className="w-11 h-11 rounded-xl gold-surface flex items-center justify-center">
                <p.icon className="w-5 h-5 text-[#0B1320]" />
              </div>
              <h3 className="font-display text-lg font-medium text-slate-100 mt-6 leading-snug">
                {p.title}
              </h3>
              <p className="text-sm text-slate-400 leading-relaxed mt-3">{p.text}</p>
            </motion.div>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.15} className="mt-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-6xl mx-auto" data-testid="solutions-comparison">
          <div className="border border-red-500/15 bg-[#141A26] rounded-2xl p-8 sm:p-10">
            <p className="font-mono-num text-[10px] uppercase tracking-[0.25em] text-red-400/90 mb-2">
              Cenário comum
            </p>
            <h3 className="font-display text-xl sm:text-2xl font-medium text-slate-300">
              Divórcio desestruturado
            </h3>
            <ul className="mt-7 space-y-4">
              {COMPARISON.map((c, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-slate-400">
                  <X className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                  {c.sem}
                </li>
              ))}
            </ul>
          </div>

          <div className="relative border border-amber-400/30 bg-gradient-to-b from-[#162238] to-[#0F1830] rounded-2xl p-8 sm:p-10 shadow-[0_0_50px_rgba(212,175,55,0.08)]">
            <div className="absolute -top-3 left-8 gold-surface text-[#0B1320] font-mono-num text-[10px] uppercase tracking-[0.2em] font-semibold px-4 py-1.5 rounded-full">
              Método TSS
            </div>
            <p className="font-mono-num text-[10px] uppercase tracking-[0.25em] text-amber-400/90 mb-2">
              Cenário TSS
            </p>
            <h3 className="font-display text-xl sm:text-2xl font-medium text-slate-100">
              Divórcio estruturado
            </h3>
            <ul className="mt-7 space-y-4">
              {COMPARISON.map((c, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-slate-200">
                  <Check className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                  {c.com}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Reveal>

      <Reveal delay={0.2} className="mt-12 text-center">
        <motion.a
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          href={waLink(WA_MESSAGES.solutions)}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="solutions-cta-whatsapp"
          className="inline-flex items-center gap-2.5 gold-surface text-[#0B1320] font-bold text-sm sm:text-base px-8 py-4 rounded-full shadow-[0_0_36px_rgba(212,175,55,0.3)] hover:shadow-[0_0_55px_rgba(212,175,55,0.5)] transition-shadow duration-300"
        >
          <MessageCircle className="w-5 h-5" />
          Fale Conosco pelo WhatsApp
        </motion.a>
      </Reveal>
    </div>
  </section>
);
